import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { C } from "../constants/colors";

export function MessagingScreen({ profile, bp }) {
  const [allMessages, setAllMessages]     = useState([]);
  const [otherProfiles, setOtherProfiles] = useState({});
  const [selectedId, setSelectedId]       = useState(null);
  const [msgInput, setMsgInput]           = useState('');
  const [sending, setSending]             = useState(false);
  const [search, setSearch]               = useState('');
  const [showCompose, setShowCompose]     = useState(false);
  const [composeSearch, setComposeSearch] = useState('');
  const [composeResults, setComposeResults] = useState([]);
  const bottomRef = useRef(null);

  /* ── load all messages for this user ── */
  const loadMessages = useCallback(async () => {
    if (!profile) return;
    const { data } = await supabase
      .from('messages')
      .select('*')
      .or(`from_user_id.eq.${profile.id},to_user_id.eq.${profile.id}`)
      .order('created_at', { ascending: true });
    if (!data) return;
    setAllMessages(data);
    const ids = [...new Set(
      data.flatMap(m => [m.from_user_id, m.to_user_id]).filter(id => id !== profile.id)
    )];
    if (ids.length === 0) return;
    const { data: profs } = await supabase
      .from('profiles')
      .select('id,display_name,full_name,avatar_url,user_type')
      .in('id', ids);
    if (profs) {
      setOtherProfiles(prev => {
        const next = { ...prev };
        profs.forEach(p => { next[p.id] = p; });
        return next;
      });
    }
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── initial load + real-time subscription ── */
  useEffect(() => {
    if (!profile) return;
    loadMessages();
    const channel = supabase.channel(`msgs-${profile.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, payload => {
        const m = payload.new;
        if (m.to_user_id === profile.id || m.from_user_id === profile.id) loadMessages();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [profile, loadMessages]);

  /* ── group messages into threads ── */
  const threads = useMemo(() => {
    if (!profile) return [];
    const map = {};
    allMessages.forEach(msg => {
      const otherId = msg.from_user_id === profile.id ? msg.to_user_id : msg.from_user_id;
      if (!map[otherId]) map[otherId] = { otherId, msgs: [], unread: 0 };
      map[otherId].msgs.push(msg);
      if (msg.to_user_id === profile.id && !msg.read) map[otherId].unread++;
    });
    return Object.values(map).sort((a, b) => {
      const aT = a.msgs[a.msgs.length - 1].created_at;
      const bT = b.msgs[b.msgs.length - 1].created_at;
      return bT.localeCompare(aT);
    });
  }, [allMessages, profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredThreads = useMemo(() => {
    if (!search.trim()) return threads;
    const q = search.toLowerCase();
    return threads.filter(t => {
      const p = otherProfiles[t.otherId];
      return (p?.display_name || p?.full_name || '').toLowerCase().includes(q);
    });
  }, [threads, search, otherProfiles]);

  const currentThread = useMemo(() => threads.find(t => t.otherId === selectedId), [threads, selectedId]);
  const currentMsgs   = currentThread?.msgs || [];
  const currentOther  = selectedId ? otherProfiles[selectedId] : null;

  /* ── open thread + mark read ── */
  const openThread = useCallback(async (otherId) => {
    if (!profile) return;
    setSelectedId(otherId);
    setMsgInput('');
    await supabase.from('messages')
      .update({ read: true })
      .eq('to_user_id', profile.id)
      .eq('from_user_id', otherId)
      .eq('read', false);
    setAllMessages(prev =>
      prev.map(m => m.from_user_id === otherId && m.to_user_id === profile.id ? { ...m, read: true } : m)
    );
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── send ── */
  const sendMsg = async () => {
    if (!msgInput.trim() || !selectedId || sending) return;
    setSending(true);
    const body = msgInput.trim();
    setMsgInput('');
    const { data } = await supabase.from('messages').insert({
      from_user_id: profile.id,
      to_user_id: selectedId,
      body,
    }).select().single();
    if (data) setAllMessages(prev => [...prev, data]);
    setSending(false);
  };

  /* ── auto-scroll ── */
  useEffect(() => {
    if (selectedId) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentMsgs.length, selectedId]);

  /* ── compose search ── */
  useEffect(() => {
    if (composeSearch.length < 2) { setComposeResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id,display_name,full_name,avatar_url,user_type')
        .neq('id', profile?.id)
        .or(`display_name.ilike.%${composeSearch}%,full_name.ilike.%${composeSearch}%`)
        .limit(10);
      setComposeResults(data || []);
    }, 280);
    return () => clearTimeout(t);
  }, [composeSearch, profile?.id]);

  const startConversation = (other) => {
    setOtherProfiles(prev => ({ ...prev, [other.id]: other }));
    setShowCompose(false);
    setComposeSearch('');
    setComposeResults([]);
    setSelectedId(other.id);
  };

  /* ── helpers ── */
  const fmtTime = (ts) => {
    const d = new Date(ts), now = new Date(), diff = now - d;
    if (diff < 60000)    return 'now';
    if (diff < 3600000)  return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
    if (diff < 604800000) return d.toLocaleDateString('en-AU', { weekday: 'short' });
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  const Av = ({ p, size = 44, radius = '50%' }) => {
    const name = p?.display_name || p?.full_name || '?';
    const ini  = name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
    return p?.avatar_url
      ? <img src={p.avatar_url} alt="" style={{ width: size, height: size, borderRadius: radius, objectFit: 'cover', flexShrink: 0 }} />
      : <div style={{ width: size, height: size, borderRadius: radius, background: `linear-gradient(135deg,${C.clay},${C.barkL})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Mono',monospace", fontSize: size > 38 ? '.78rem' : '.62rem', color: '#fff', flexShrink: 0 }}>{ini}</div>;
  };

  const isMobile   = bp === 'mobile';
  const showList   = !isMobile || !selectedId;
  const showThread = !isMobile || !!selectedId;

  return (
    <div>
      {/* ── page header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <h1 style={{ fontFamily: "'Fraunces',serif", fontSize: '1.7rem', fontWeight: 400, color: C.bark, margin: 0 }}>Messages</h1>
        <button
          onClick={() => setShowCompose(true)}
          title="New message"
          style={{ background: C.bark, color: '#f0e2cc', border: 'none', borderRadius: 10, width: 42, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '1.05rem' }}
        >✏</button>
      </div>

      {/* ── split panel ── */}
      <div style={{ display: 'flex', border: `1px solid ${C.sand}`, borderRadius: 12, overflow: 'hidden', minHeight: 520, background: C.cream }}>

        {/* ─ thread list ─ */}
        {showList && (
          <div style={{ width: isMobile ? '100%' : 272, borderRight: isMobile ? 'none' : `1px solid ${C.sand}`, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
            <div style={{ padding: '12px 12px 10px', borderBottom: `1px solid ${C.parchDD}` }}>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: '.72rem', color: C.mutedL, pointerEvents: 'none' }}>🔍</span>
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search messages…"
                  style={{ width: '100%', paddingLeft: 30, paddingRight: 10, paddingTop: 8, paddingBottom: 8, border: `1px solid ${C.parchDD}`, borderRadius: 20, fontSize: '.78rem', color: C.bark, background: C.parch, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {filteredThreads.length === 0 ? (
                <div style={{ padding: '36px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: 10, opacity: .4 }}>✉</div>
                  <div style={{ fontFamily: "'Fraunces',serif", fontSize: '.9rem', color: C.bark, marginBottom: 6 }}>{search ? 'No results' : 'No messages yet'}</div>
                  {!search && <div style={{ fontSize: '.74rem', color: C.muted, lineHeight: 1.6 }}>Tap ✏ to start a conversation with any artist or gallery.</div>}
                </div>
              ) : filteredThreads.map(t => {
                const other   = otherProfiles[t.otherId];
                const name    = other?.display_name || other?.full_name || 'User';
                const lastMsg = t.msgs[t.msgs.length - 1];
                const isOwn   = lastMsg.from_user_id === profile.id;
                const isSel   = selectedId === t.otherId;
                return (
                  <button
                    key={t.otherId}
                    onClick={() => openThread(t.otherId)}
                    style={{ width: '100%', display: 'flex', gap: 11, alignItems: 'center', padding: '11px 13px', background: isSel ? C.parchD : 'transparent', border: 'none', borderBottom: `1px solid ${C.parchDD}`, cursor: 'pointer', textAlign: 'left' }}
                  >
                    <Av p={other} size={44} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6, marginBottom: 2 }}>
                        <span style={{ fontFamily: "'Fraunces',serif", fontSize: '.88rem', color: C.bark, fontWeight: t.unread > 0 ? 700 : 400, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{name}</span>
                        <span style={{ fontSize: '.58rem', color: C.mutedL, fontFamily: "'DM Mono',monospace", flexShrink: 0 }}>{fmtTime(lastMsg.created_at)}</span>
                      </div>
                      <div style={{ fontSize: '.74rem', color: t.unread > 0 ? C.bark : C.muted, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontWeight: t.unread > 0 ? 600 : 400 }}>
                        {isOwn ? 'You: ' : ''}{lastMsg.body}
                      </div>
                    </div>
                    {t.unread > 0 && (
                      <div style={{ width: 18, height: 18, borderRadius: '50%', background: C.clay, color: '#fff', fontSize: '.58rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Mono',monospace", flexShrink: 0, fontWeight: 700 }}>
                        {t.unread > 9 ? '9+' : t.unread}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ─ thread detail ─ */}
        {showThread && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {selectedId ? (
              <>
                {/* header */}
                <div style={{ padding: '11px 15px', borderBottom: `1px solid ${C.sand}`, display: 'flex', alignItems: 'center', gap: 10, background: C.cream, flexShrink: 0 }}>
                  {isMobile && (
                    <button onClick={() => setSelectedId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: '1.1rem', minWidth: 28, padding: 0 }}>←</button>
                  )}
                  <Av p={currentOther} size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "'Fraunces',serif", fontSize: '.92rem', color: C.bark, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                      {currentOther?.display_name || currentOther?.full_name || 'User'}
                    </div>
                    <div style={{ fontSize: '.6rem', color: C.muted, fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '1px' }}>
                      {currentOther?.user_type === 'gallery' ? 'Gallery' : 'Artist'}
                    </div>
                  </div>
                </div>

                {/* bubbles */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {currentMsgs.map((msg, i) => {
                    const isOwn    = msg.from_user_id === profile.id;
                    const prevSame = i > 0 && currentMsgs[i - 1].from_user_id === msg.from_user_id;
                    const nextDiff = !currentMsgs[i + 1] || currentMsgs[i + 1].from_user_id !== msg.from_user_id;
                    return (
                      <div key={msg.id} style={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start', marginTop: prevSame ? 1 : 8 }}>
                        {!isOwn && (
                          <div style={{ width: 28, marginRight: 6, flexShrink: 0, alignSelf: 'flex-end' }}>
                            {!prevSame && <Av p={currentOther} size={28} />}
                          </div>
                        )}
                        <div style={{ maxWidth: '70%' }}>
                          <div style={{
                            background: isOwn ? C.bark : C.parchD,
                            color: isOwn ? '#f0e2cc' : C.bark,
                            borderRadius: isOwn
                              ? (prevSame ? '18px 4px 4px 18px' : '18px 18px 4px 18px')
                              : (prevSame ? '4px 18px 18px 4px' : '18px 18px 18px 4px'),
                            padding: '9px 14px',
                            fontSize: '.84rem',
                            lineHeight: 1.5,
                            wordBreak: 'break-word',
                          }}>
                            {msg.body}
                          </div>
                          {nextDiff && (
                            <div style={{ fontSize: '.58rem', color: C.mutedL, fontFamily: "'DM Mono',monospace", marginTop: 3, textAlign: isOwn ? 'right' : 'left', paddingLeft: isOwn ? 0 : 4, paddingRight: isOwn ? 4 : 0 }}>
                              {fmtTime(msg.created_at)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>

                {/* input */}
                <div style={{ padding: '10px 12px', borderTop: `1px solid ${C.sand}`, display: 'flex', gap: 8, background: C.cream, flexShrink: 0 }}>
                  <input
                    value={msgInput}
                    onChange={e => setMsgInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMsg(); } }}
                    placeholder="Message…"
                    style={{ flex: 1, border: `1px solid ${C.sand}`, borderRadius: 22, padding: '9px 16px', fontSize: '.84rem', color: C.bark, background: C.parch, outline: 'none' }}
                  />
                  <button
                    onClick={sendMsg}
                    disabled={!msgInput.trim() || sending}
                    style={{ background: msgInput.trim() ? C.clay : C.sand, color: msgInput.trim() ? '#fff' : C.mutedL, border: 'none', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: msgInput.trim() ? 'pointer' : 'default', flexShrink: 0, fontSize: '.95rem', transition: 'background .15s' }}
                  >→</button>
                </div>
              </>
            ) : (
              /* desktop empty state */
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24 }}>
                <div style={{ width: 72, height: 72, borderRadius: '50%', background: C.parchD, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem' }}>✉</div>
                <div style={{ fontFamily: "'Fraunces',serif", fontSize: '1.1rem', color: C.bark }}>Your messages</div>
                <div style={{ fontSize: '.82rem', color: C.muted, textAlign: 'center', maxWidth: 260, lineHeight: 1.65 }}>Send private messages to any artist or gallery on opencall.</div>
                <button className="btn-p" onClick={() => setShowCompose(true)} style={{ marginTop: 6, fontSize: '.72rem', padding: '9px 20px' }}>Start a conversation</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── compose modal ── */}
      {showCompose && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(30,20,16,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
          onClick={() => { setShowCompose(false); setComposeSearch(''); setComposeResults([]); }}
        >
          <div
            style={{ background: C.parch, borderRadius: 16, width: '100%', maxWidth: 420, padding: '22px 22px 18px', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: '1.25rem', fontWeight: 400, color: C.bark, margin: 0 }}>New message</h2>
              <button onClick={() => { setShowCompose(false); setComposeSearch(''); setComposeResults([]); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: '1.1rem', padding: 4 }}>✕</button>
            </div>
            <div style={{ position: 'relative', marginBottom: 14 }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: '.78rem', color: C.mutedL, pointerEvents: 'none' }}>🔍</span>
              <input
                autoFocus
                value={composeSearch}
                onChange={e => setComposeSearch(e.target.value)}
                placeholder="Search people on opencall…"
                style={{ width: '100%', paddingLeft: 36, paddingRight: 12, paddingTop: 11, paddingBottom: 11, border: `1px solid ${C.sand}`, borderRadius: 8, fontSize: '.84rem', color: C.bark, background: C.cream, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {composeSearch.length < 2 ? (
                <div style={{ padding: '28px 0', textAlign: 'center', color: C.mutedL, fontSize: '.8rem' }}>Type a name to search…</div>
              ) : composeResults.length === 0 ? (
                <div style={{ padding: '28px 0', textAlign: 'center', color: C.mutedL, fontSize: '.8rem' }}>No results found</div>
              ) : composeResults.map(u => {
                const name = u.display_name || u.full_name || 'User';
                return (
                  <button
                    key={u.id}
                    onClick={() => startConversation(u)}
                    style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '9px 10px', background: 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer', textAlign: 'left', width: '100%' }}
                    onMouseOver={e => e.currentTarget.style.background = C.parchD}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <Av p={u} size={40} />
                    <div>
                      <div style={{ fontFamily: "'Fraunces',serif", fontSize: '.88rem', color: C.bark }}>{name}</div>
                      <div style={{ fontSize: '.62rem', color: C.muted, fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '1px' }}>{u.user_type === 'gallery' ? 'Gallery' : 'Artist'}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
